import { PLATFORM_ORGANIZATION_ADMIN_API, adminDomainListSchema, adminDomainSchema, auditPageSchema, domainCreatedSchema, effectiveModuleSchema, emailDomainListSchema, emailDomainSchema, invitationListSchema, invitationSummarySchema, memberListSchema, memberSchema, moduleConfigurationResultSchema, organizationBillingDocumentListSchema, organizationBillingSchema, organizationContractSchema, organizationEmergencyStopListSchema, organizationEmergencyStopSchema, organizationIntelligenceLevelSchema, organizationModulesSchema, organizationOverviewSchema, organizationPeriodUsageSchema, organizationRatedPeriodSchema, paymentSessionSchema, billingPaymentMethodSchema, } from '@traceio-org/platform-contracts';
/** Route templates for error reports (PLATFORM_ORGANIZATION_ADMIN_API builds the paths). */
const ORGANIZATION = '/v1/organizations/:organizationId';
const ROUTES = {
    overview: ORGANIZATION,
    members: `${ORGANIZATION}/members`,
    member: `${ORGANIZATION}/members/:membershipId`,
    invitations: `${ORGANIZATION}/invitations`,
    revokeInvitation: `${ORGANIZATION}/invitations/:invitationId/revoke`,
    modules: `${ORGANIZATION}/modules`,
    module: `${ORGANIZATION}/modules/:moduleKey`,
    intelligenceLevel: `${ORGANIZATION}/intelligence-level`,
    emergencyStops: `${ORGANIZATION}/emergency-stops`,
    releaseEmergencyStop: `${ORGANIZATION}/emergency-stops/:stopId/release`,
    domains: `${ORGANIZATION}/domains`,
    domainAction: `${ORGANIZATION}/domains/:domainId/actions`,
    emailDomains: `${ORGANIZATION}/email-domains`,
    emailDomainAction: `${ORGANIZATION}/email-domains/:emailDomainId/actions`,
    audit: `${ORGANIZATION}/audit`,
    billing: `${ORGANIZATION}/billing`,
    billingUsage: `${ORGANIZATION}/billing/usage`,
    billingLineItems: `${ORGANIZATION}/billing/line-items`,
    billingDocuments: `${ORGANIZATION}/billing/documents`,
    billingSpendCap: `${ORGANIZATION}/billing/contracts/:contractId/spend-cap`,
    billingCarrierConnections: `${ORGANIZATION}/billing/contracts/:contractId/carrier-connections`,
    billingPaymentSession: `${ORGANIZATION}/billing/payment-session`,
    billingPaymentMethod: `${ORGANIZATION}/billing/payment-method`,
};
/**
 * Which client method serves which operation of ORGANIZATION_ADMIN_OPERATIONS, so a test can hold the client to
 * the contract's route table (one method per route and verb, none missing).
 */
export const ORGANIZATION_CLIENT_METHODS = {
    readOverview: 'orgOverview',
    listMembers: 'orgMembers',
    readMember: 'orgMember',
    changeMember: 'orgUpdateMember',
    listInvitations: 'orgInvitations',
    createInvitation: 'orgCreateInvitation',
    revokeInvitation: 'orgRevokeInvitation',
    listModules: 'orgModules',
    readModule: 'orgModule',
    configureModule: 'orgUpdateModule',
    setIntelligenceLevel: 'orgSetIntelligenceLevel',
    listEmergencyStops: 'orgEmergencyStops',
    createEmergencyStop: 'orgStartEmergencyStop',
    releaseEmergencyStop: 'orgReleaseEmergencyStop',
    listDomains: 'orgDomains',
    createDomain: 'orgCreateDomain',
    actOnDomain: 'orgDomainAction',
    listEmailDomains: 'orgEmailDomains',
    addEmailDomain: 'orgAddEmailDomain',
    actOnEmailDomain: 'orgEmailDomainAction',
    readAudit: 'orgAudit',
    readBilling: 'orgBilling',
    readBillingUsage: 'orgBillingUsage',
    readBillingLineItems: 'orgBillingLineItems',
    readBillingDocuments: 'orgBillingDocuments',
    changeSpendCap: 'orgUpdateSpendCap',
    changeCarrierConnections: 'orgUpdateCarrierConnections',
    createPaymentSession: 'orgCreatePaymentSession',
    readPaymentMethod: 'orgPaymentMethod',
};
export function createOrganizationMethods(request) {
    // Every route here takes a person token for the platform audience: the call's own or getAccessToken's.
    const call = (spec, options) => request({ ...spec, auth: 'bearer', signal: options?.signal, accessToken: options?.accessToken });
    const api = PLATFORM_ORGANIZATION_ADMIN_API;
    return {
        orgOverview: (organizationId, options) => call({
            route: ROUTES.overview,
            path: api.overview(organizationId),
            method: 'GET',
            schema: organizationOverviewSchema,
        }, options),
        orgMembers: (organizationId, query, options) => call({
            route: ROUTES.members,
            path: api.members(organizationId),
            method: 'GET',
            query: {
                query: query?.query,
                status: query?.status,
                productKey: query?.productKey,
                cursor: query?.cursor,
                limit: query?.limit,
            },
            schema: memberListSchema,
        }, options),
        orgMember: (organizationId, membershipId, options) => call({
            route: ROUTES.member,
            path: api.member(organizationId, membershipId),
            method: 'GET',
            schema: memberSchema,
        }, options),
        orgUpdateMember: (organizationId, membershipId, body, options) => call({
            route: ROUTES.member,
            path: api.member(organizationId, membershipId),
            method: 'PATCH',
            body,
            schema: memberSchema,
        }, options),
        orgInvitations: (organizationId, options) => call({
            route: ROUTES.invitations,
            path: api.invitations(organizationId),
            method: 'GET',
            schema: invitationListSchema,
        }, options),
        orgCreateInvitation: (organizationId, body, options) => call({
            route: ROUTES.invitations,
            path: api.invitations(organizationId),
            method: 'POST',
            body,
            schema: invitationSummarySchema,
        }, options),
        orgRevokeInvitation: (organizationId, invitationId, body, options) => call({
            route: ROUTES.revokeInvitation,
            path: api.revokeInvitation(organizationId, invitationId),
            method: 'POST',
            body,
            schema: invitationSummarySchema,
        }, options),
        orgModules: (organizationId, options) => call({
            route: ROUTES.modules,
            path: api.modules(organizationId),
            method: 'GET',
            schema: organizationModulesSchema,
        }, options),
        orgModule: (organizationId, moduleKey, options) => call({
            route: ROUTES.module,
            path: api.module(organizationId, moduleKey),
            method: 'GET',
            schema: effectiveModuleSchema,
        }, options),
        orgUpdateModule: (organizationId, moduleKey, body, options) => call({
            route: ROUTES.module,
            path: api.module(organizationId, moduleKey),
            method: 'PATCH',
            body,
            schema: moduleConfigurationResultSchema,
        }, options),
        orgSetIntelligenceLevel: (organizationId, body, options) => call({
            route: ROUTES.intelligenceLevel,
            path: api.intelligenceLevel(organizationId),
            method: 'PATCH',
            body,
            schema: organizationIntelligenceLevelSchema,
        }, options),
        orgEmergencyStops: (organizationId, options) => call({
            route: ROUTES.emergencyStops,
            path: api.emergencyStops(organizationId),
            method: 'GET',
            schema: organizationEmergencyStopListSchema,
        }, options),
        orgStartEmergencyStop: (organizationId, body, options) => call({
            route: ROUTES.emergencyStops,
            path: api.emergencyStops(organizationId),
            method: 'POST',
            body,
            schema: organizationEmergencyStopSchema,
        }, options),
        orgReleaseEmergencyStop: (organizationId, stopId, body, options) => call({
            route: ROUTES.releaseEmergencyStop,
            path: api.releaseEmergencyStop(organizationId, stopId),
            method: 'POST',
            body,
            schema: organizationEmergencyStopSchema,
        }, options),
        orgDomains: (organizationId, options) => call({
            route: ROUTES.domains,
            path: api.domains(organizationId),
            method: 'GET',
            schema: adminDomainListSchema,
        }, options),
        orgCreateDomain: (organizationId, body, options) => call({
            route: ROUTES.domains,
            path: api.domains(organizationId),
            method: 'POST',
            body,
            schema: domainCreatedSchema,
        }, options),
        orgDomainAction: (organizationId, domainId, body, options) => call({
            route: ROUTES.domainAction,
            path: api.domainAction(organizationId, domainId),
            method: 'POST',
            body,
            schema: adminDomainSchema,
        }, options),
        orgEmailDomains: (organizationId, options) => call({
            route: ROUTES.emailDomains,
            path: api.emailDomains(organizationId),
            method: 'GET',
            schema: emailDomainListSchema,
        }, options),
        orgAddEmailDomain: (organizationId, body, options) => call({
            route: ROUTES.emailDomains,
            path: api.emailDomains(organizationId),
            method: 'POST',
            body,
            schema: emailDomainSchema,
        }, options),
        orgEmailDomainAction: (organizationId, emailDomainId, body, options) => call({
            route: ROUTES.emailDomainAction,
            path: api.emailDomainAction(organizationId, emailDomainId),
            method: 'POST',
            body,
            schema: emailDomainSchema,
        }, options),
        orgAudit: (organizationId, query, options) => call({
            route: ROUTES.audit,
            path: api.audit(organizationId),
            method: 'GET',
            query: {
                action: query?.action,
                actorId: query?.actorId,
                from: query?.from,
                to: query?.to,
                cursor: query?.cursor,
                limit: query?.limit,
            },
            schema: auditPageSchema,
        }, options),
        orgBilling: (organizationId, options) => call({
            route: ROUTES.billing,
            path: api.billing(organizationId),
            method: 'GET',
            schema: organizationBillingSchema,
        }, options),
        orgBillingUsage: (organizationId, query, options) => call({
            route: ROUTES.billingUsage,
            path: api.billingUsage(organizationId),
            method: 'GET',
            query: { contractId: query?.contractId, periodStart: query?.periodStart },
            schema: organizationPeriodUsageSchema,
        }, options),
        orgBillingLineItems: (organizationId, query, options) => call({
            route: ROUTES.billingLineItems,
            path: api.billingLineItems(organizationId),
            method: 'GET',
            query: { contractId: query?.contractId, periodStart: query?.periodStart },
            schema: organizationRatedPeriodSchema,
        }, options),
        orgBillingDocuments: (organizationId, options) => call({
            route: ROUTES.billingDocuments,
            path: api.billingDocuments(organizationId),
            method: 'GET',
            schema: organizationBillingDocumentListSchema,
        }, options),
        orgUpdateSpendCap: (organizationId, contractId, body, options) => call({
            route: ROUTES.billingSpendCap,
            path: api.billingSpendCap(organizationId, contractId),
            method: 'PATCH',
            body,
            schema: organizationContractSchema,
        }, options),
        orgUpdateCarrierConnections: (organizationId, contractId, body, options) => call({
            route: ROUTES.billingCarrierConnections,
            path: api.billingCarrierConnections(organizationId, contractId),
            method: 'PATCH',
            body,
            schema: organizationContractSchema,
        }, options),
        orgCreatePaymentSession: (organizationId, options) => call({
            route: ROUTES.billingPaymentSession,
            path: api.billingPaymentSession(organizationId),
            method: 'POST',
            schema: paymentSessionSchema,
        }, options),
        orgPaymentMethod: (organizationId, query, options) => call({
            route: ROUTES.billingPaymentMethod,
            path: api.billingPaymentMethod(organizationId),
            method: 'GET',
            query: { fresh: query?.fresh ? 'true' : undefined },
            schema: billingPaymentMethodSchema,
        }, options),
    };
}
//# sourceMappingURL=organization.js.map